import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ChevronRight,
  ClipboardCheck,
  Coins,
  FileText,
  HandCoins,
  History,
  LayoutDashboard,
  Link2,
  Mail,
  Lock,
  Package,
  PackageCheck,
  QrCode,
  RotateCcw,
  Settings as SettingsIcon,
  Store,
  TrendingUp,
  UserRoundCog,
  Users,
  Warehouse,
  type LucideIcon,
} from "lucide-react";
import {
  DEFAULT_PERMISSIONS,
  MODULE_FEATURE,
  grantable,
  MODULE_META,
  PERMISSION_ACTIONS,
  ROLE_LABELS,
  type OrgRolesDto,
  type PermissionAction,
  type PermissionGrant,
  type PermissionGrid,
  type PermissionModule,
  type PermissionScope,
  type RoleDef,
} from "@gatiq/shared";
import { Badge, Button, Card, CardBody, EmptyState, Skeleton, useToast } from "@/ui";
import { TabBarPortal } from "@/layout/PageChrome";
import { api, ApiClientError } from "@/lib/api";
import { invalidateOrgRoles } from "@/auth/useOrgRoles";

/** The sidebar's own groups, in its order, so an admin finds a permission where the page lives. */
const SECTIONS: { key: string; label: string; modules: PermissionModule[]; adminOnly?: string[] }[] = [
  { key: "dashboards", label: "Dashboards", modules: ["overview", "performance"] },
  { key: "network", label: "Network", modules: ["depots", "team", "counters", "karigars", "approvals"] },
  { key: "rewards", label: "Rewards", modules: ["products", "rewards", "dispatch", "payouts", "settlements", "documents", "broadcasts"] },
  // Settings holds this very screen, so granting it would let anyone grant themselves anything.
  { key: "organisation", label: "Organisation", modules: ["links", "audit"], adminOnly: ["Settings"] },
];

/** The same icons as the sidebar. */
const MODULE_ICON: Record<PermissionModule, LucideIcon> = {
  overview: LayoutDashboard,
  links: Link2,
  team: UserRoundCog,
  counters: Store,
  karigars: Users,
  approvals: ClipboardCheck,
  depots: Warehouse,
  products: Package,
  rewards: QrCode,
  payouts: Coins,
  settlements: HandCoins,
  performance: TrendingUp,
  audit: History,
  dispatch: PackageCheck,
  broadcasts: Mail,
  documents: FileText,
};

const MODULE_LABEL: Partial<Record<PermissionModule, string>> = {
  team: "Team & hierarchy",
  performance: "Performance Tracker",
};
const moduleLabel = (m: PermissionModule) => MODULE_LABEL[m] ?? MODULE_META[m].label;

/** Each permission as a plain sentence. */
const PERMISSION_LABEL: Record<PermissionModule, Partial<Record<PermissionAction, string>>> = {
  overview: { view: "View the overview dashboard" },
  links: { view: "View & share sign-in and registration links" },
  team: {
    view: "View people below them",
    create: "Add people below them",
    edit: "Edit & suspend people below them",
    delete: "Remove people below them",
  },
  counters: { view: "View counters", create: "Add counters", edit: "Edit counters", delete: "Delete counters" },
  karigars: {
    view: "View karigars",
    create: "Add karigars",
    edit: "Edit & transfer karigars",
    delete: "Delete karigars",
  },
  approvals: { view: "View KYC approvals", edit: "Approve / reject KYC" },
  dispatch: { view: "View dispatches & stock", create: "Send stock", delete: "Undo dispatches" },
  depots: { view: "View depots", create: "Add depots", edit: "Edit depots", delete: "Delete depots" },
  products: { view: "View products", create: "Add products", edit: "Edit products" },
  rewards: {
    view: "View reward codes",
    create: "Issue code batches",
    edit: "Edit & reverse codes",
    delete: "Withdraw batches",
  },
  payouts: { view: "View payouts", create: "Pay karigars" },
  settlements: { view: "View settlements", create: "Record settlement payments" },
  performance: { view: "View performance" },
  audit: { view: "View audit log" },
  broadcasts: { view: "View promotions", create: "Send promotions" },
  documents: {
    view: "View documents",
    create: "Upload documents",
    edit: "Edit & replace documents",
    delete: "Delete documents",
  },
};

interface Draft {
  name: string;
  permissions: PermissionGrid;
  /** "Reset to defaults" was pressed: save as `null` so the role follows future defaults. */
  reset: boolean;
}

/**
 * Sets one permission and keeps the module coherent: acting on something means
 * seeing it at least as far, and narrowing what someone sees narrows what they
 * can do. Without this an admin could grant "edit everyone" to a role that can
 * only see its own chain.
 */
function withGrant(
  grid: PermissionGrid,
  module: PermissionModule,
  action: PermissionAction,
  scope: PermissionScope | null,
): PermissionGrid {
  const grant: PermissionGrant = { ...grid[module] };
  if (action === "view") {
    if (!scope) {
      const rest = { ...grid };
      delete rest[module];
      return rest;
    }
    grant.view = scope;
    if (scope === "own") for (const a of PERMISSION_ACTIONS) if (grant[a]) grant[a] = "own";
  } else {
    if (scope) grant[action] = scope;
    else delete grant[action];
    if (scope && (!grant.view || (scope === "org" && grant.view === "own"))) grant.view = scope;
  }
  return { ...grid, [module]: grant };
}

/** Every permission of a module on — at the reach already chosen, else the narrower one. */
function withModule(
  grid: PermissionGrid,
  module: PermissionModule,
  on: boolean,
  actions: readonly PermissionAction[] = MODULE_META[module].actions,
): PermissionGrid {
  if (!on) {
    const rest = { ...grid };
    delete rest[module];
    return rest;
  }
  const meta = MODULE_META[module];
  const scope: PermissionScope = meta.chain ? (grid[module]?.view ?? "own") : "org";
  return { ...grid, [module]: Object.fromEntries(actions.map((a) => [a, grid[module]?.[a] ?? scope])) };
}

const countOn = (
  grid: PermissionGrid,
  module: PermissionModule,
  actions: readonly PermissionAction[] = MODULE_META[module].actions,
) => actions.filter((a) => grid[module]?.[a]).length;

type Tri = "all" | "some" | "none";
function tri(on: number, total: number): Tri {
  return on === 0 ? "none" : on === total ? "all" : "some";
}

/** What a copied level reports to, or what a renamed role is underneath — nothing when the name says it. */
function subtitle(role: RoleDef, name: string, managerName: string | null): string | null {
  if (managerName) return `Reports to ${managerName}`;
  const base = ROLE_LABELS[role.base];
  return name === base ? null : base;
}

/**
 * Settings → Roles & permissions (D-16). Pick a role, then browse the
 * sidebar's sections → pages → permissions, the way the app itself is laid
 * out. Anything inside a person's network carries a reach: their own chain,
 * or the whole organization. Every change is saved together from the tab row.
 */
export function RolesPermissionsPanel() {
  const toast = useToast();
  const [data, setData] = useState<OrgRolesDto | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [sectionKey, setSectionKey] = useState("network");
  const [module, setModule] = useState<PermissionModule>("counters");
  const [saving, setSaving] = useState(false);

  function adopt(res: OrgRolesDto): void {
    setData(res);
    setDrafts(Object.fromEntries(res.roles.map((r) => [r.key, { name: r.name, permissions: r.permissions, reset: false }])));
  }

  useEffect(() => {
    let live = true;
    api
      .get<OrgRolesDto>("/org/roles")
      .then((res) => {
        if (!live) return;
        adopt(res);
        setSelectedKey((key) => key ?? res.roles[0]?.key ?? null);
      })
      .catch((err: unknown) => toast.error(err instanceof ApiClientError ? err.message : "Could not load roles."));
    return () => {
      live = false;
    };
  }, [toast]);

  const roles = data?.roles ?? [];
  // A switched-off module (D-20) has nothing to grant.
  // Only what the selected role can actually use: no stock for a sales rep, no team for a counter.
  const selectedBase = roles.find((r) => r.key === selectedKey)?.base;
  const actionsOf = (m: PermissionModule): PermissionAction[] =>
    MODULE_META[m].actions.filter((a) => !selectedBase || grantable(selectedBase, m, a));
  const sections = SECTIONS.map((s) => ({
    ...s,
    modules: s.modules.filter((m) => {
      const feature = MODULE_FEATURE[m];
      return (!feature || Boolean(data?.features?.[feature])) && actionsOf(m).length > 0;
    }),
  }));
  const shownName = (key: string): string => drafts[key]?.name.trim() || roles.find((r) => r.key === key)?.name || key;
  const isDirty = (role: RoleDef): boolean => {
    const draft = drafts[role.key];
    if (!draft) return false;
    const gridChanged = draft.reset
      ? role.customized
      : JSON.stringify(draft.permissions) !== JSON.stringify(role.permissions);
    return draft.name.trim() !== role.name || gridChanged;
  };
  const dirty = roles.filter(isDirty);

  const selected = roles.find((r) => r.key === selectedKey) ?? null;
  const draft = selected ? drafts[selected.key] : undefined;
  const grid = draft?.permissions ?? {};
  const section = sections.find((s) => s.key === sectionKey) ?? sections[0]!;

  function update(change: Partial<Draft>): void {
    if (!selected) return;
    setDrafts((all) => ({ ...all, [selected.key]: { ...all[selected.key]!, ...change } }));
  }
  const setGrid = (permissions: PermissionGrid) => update({ permissions, reset: false });

  async function save(): Promise<void> {
    const bad = dirty.find((r) => (drafts[r.key]?.name.trim().length ?? 0) < 2);
    if (bad) {
      setSelectedKey(bad.key);
      toast.error("Every role needs a name of at least 2 characters.");
      return;
    }
    setSaving(true);
    try {
      for (const role of dirty) {
        const d = drafts[role.key]!;
        const body: { name?: string; permissions?: PermissionGrid | null } = {};
        if (d.name.trim() !== role.name) body.name = d.name.trim();
        if (d.reset) {
          if (role.customized) body.permissions = null;
        } else if (JSON.stringify(d.permissions) !== JSON.stringify(role.permissions)) {
          body.permissions = d.permissions;
        }
        await api.patch<RoleDef>(`/org/roles/${encodeURIComponent(role.key)}`, body);
      }
      adopt(await api.get<OrgRolesDto>("/org/roles"));
      invalidateOrgRoles();
      toast.success(dirty.length === 1 ? "Role saved." : `${dirty.length} roles saved.`);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not save roles.");
    } finally {
      setSaving(false);
    }
  }

  if (!data) {
    return (
      <Card>
        <CardBody className="flex flex-col gap-3">
          <Skeleton className="h-9 w-2/3" />
          <Skeleton className="h-64 w-full" />
        </CardBody>
      </Card>
    );
  }

  if (roles.length === 0) {
    return (
      <Card>
        <EmptyState message="Add roles to your chain in the General tab first." title="No roles to configure" />
      </Card>
    );
  }

  const sectionOn = section.modules.reduce((n, m) => n + countOn(grid, m, actionsOf(m)), 0);
  const sectionTotal = section.modules.reduce((n, m) => n + actionsOf(m).length, 0);
  const ownLabel = selected?.base === "counter" ? "Own shop" : "Own chain";

  return (
    <>
      <TabBarPortal>
        {dirty.length > 0 ? (
          <span className="text-xs text-muted">
            {dirty.length} unsaved {dirty.length === 1 ? "role" : "roles"}
          </span>
        ) : null}
        <Button disabled={dirty.length === 0} loading={saving} onClick={() => void save()} size="sm">
          Save
        </Button>
      </TabBarPortal>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto md:overflow-hidden">
        {/* ── which role ─────────────────────────────────────────── */}
        <div aria-label="Roles" className="flex shrink-0 flex-wrap gap-1.5" role="tablist">
          {roles.map((role) => {
            const active = role.key === selectedKey;
            const name = shownName(role.key);
            const sub = subtitle(role, name, role.reportsTo ? shownName(role.reportsTo) : null);
            return (
              <button
                aria-selected={active}
                className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-left ring-1 transition-colors ring-inset ${
                  active ? "bg-brand-light ring-brand-border" : "bg-raised ring-rule hover:bg-ink/[0.03]"
                }`}
                key={role.key}
                onClick={() => setSelectedKey(role.key)}
                role="tab"
                type="button"
              >
                <span className="min-w-0">
                  <span className={`block truncate text-sm font-medium ${active ? "text-brand-ink" : "text-ink"}`}>
                    {name}
                  </span>
                  {sub ? <span className="block truncate text-[11px] text-muted">{sub}</span> : null}
                </span>
                {isDirty(role) ? (
                  <Badge tone="warning">Unsaved</Badge>
                ) : role.customized ? (
                  <Badge tone="brand">Custom</Badge>
                ) : null}
              </button>
            );
          })}
        </div>

        {selected && draft ? (
          <>
            <div className="flex shrink-0 flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-[13px] font-medium text-ink" htmlFor="role-name">
                Role name
                <input
                  className="h-9 w-64 rounded-md border border-rule bg-raised px-3 text-sm font-normal text-ink shadow-control transition-[border-color,box-shadow] hover:border-ink/25 focus:border-brand focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] focus:outline-none"
                  id="role-name"
                  maxLength={120}
                  onChange={(e) => update({ name: e.target.value })}
                  value={draft.name}
                />
              </label>
              <Button
                icon={<RotateCcw aria-hidden="true" className="size-4" />}
                onClick={() => update({ permissions: DEFAULT_PERMISSIONS[selected.base], reset: true })}
                size="sm"
                variant="secondary"
              >
                Reset to defaults
              </Button>
            </div>

            {/* ── section → page → permission ────────────────────── */}
            <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden rounded-lg border border-rule bg-raised shadow-card md:grid-cols-[13rem_17rem_minmax(0,1fr)]">
              <Column title="Module">
                {sections.map((s) => {
                  const on = s.modules.reduce((n, m) => n + countOn(grid, m, actionsOf(m)), 0);
                  return (
                    <NavRow
                      active={s.key === section.key}
                      count={on > 0 ? String(on) : null}
                      key={s.key}
                      label={s.label}
                      onClick={() => {
                        setSectionKey(s.key);
                        setModule(s.modules[0]!);
                      }}
                      strong={on > 0}
                    />
                  );
                })}
              </Column>

              <Column title={section.label}>
                <CheckRow
                  label="Select all"
                  onToggle={(on) => setGrid(section.modules.reduce((g, m) => withModule(g, m, on, actionsOf(m)), grid))}
                  state={tri(sectionOn, sectionTotal)}
                />
                {section.modules.map((m) => {
                  const on = countOn(grid, m, actionsOf(m));
                  const total = actionsOf(m).length;
                  return (
                    <NavRow
                      active={m === module}
                      check={{ state: tri(on, total), onToggle: (next) => setGrid(withModule(grid, m, next, actionsOf(m))) }}
                      count={`${on}/${total}`}
                      icon={MODULE_ICON[m]}
                      key={m}
                      label={moduleLabel(m)}
                      onClick={() => setModule(m)}
                      strong={on > 0}
                    />
                  );
                })}
                {(section.adminOnly ?? []).map((label) => (
                  <div
                    className="flex min-h-11 items-center gap-2.5 border-b border-l-2 border-rule/60 border-l-transparent pr-3 pl-3.5 text-sm text-muted"
                    key={label}
                  >
                    <Lock aria-hidden="true" className="size-4 shrink-0" />
                    <SettingsIcon aria-hidden="true" className="size-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    <span className="shrink-0 rounded-full bg-ink/[0.05] px-2 py-0.5 text-[11px] font-semibold">
                      Admin only
                    </span>
                  </div>
                ))}
              </Column>

              <Column title={moduleLabel(module)}>
                {actionsOf(module).map((action) => {
                  const scope = grid[module]?.[action] ?? null;
                  const label = PERMISSION_LABEL[module][action] ?? action;
                  return (
                    <div
                      className="flex min-h-11 flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-b border-rule/60 px-4 py-2"
                      key={action}
                    >
                      <CheckRow
                        bare
                        label={label}
                        onToggle={(on) =>
                          setGrid(
                            withGrant(grid, module, action, on ? (MODULE_META[module].chain ? (grid[module]?.view ?? "own") : "org") : null),
                          )
                        }
                        state={scope ? "all" : "none"}
                      />
                      {scope && MODULE_META[module].chain ? (
                        <div aria-label={`${label}: reach`} className="flex rounded-md bg-ink/[0.05] p-0.5" role="radiogroup">
                          {(["own", "org"] as const).map((s) => (
                            <button
                              aria-checked={scope === s}
                              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                                scope === s ? "bg-raised text-ink shadow-sm" : "text-muted hover:text-ink"
                              }`}
                              key={s}
                              onClick={() => setGrid(withGrant(grid, module, action, s))}
                              role="radio"
                              type="button"
                            >
                              {s === "own" ? ownLabel : "Whole org"}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </Column>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}

function Column({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex min-h-0 flex-col border-b border-rule last:border-b-0 md:border-r md:border-b-0 md:last:border-r-0">
      <h3 className="shrink-0 border-b border-rule bg-ink/[0.025] px-4 py-2.5 text-[11px] font-semibold tracking-[0.08em] text-muted uppercase">
        {title}
      </h3>
      <div className="min-h-0 flex-1 md:overflow-y-auto">{children}</div>
    </section>
  );
}

/** A row that opens the next column, optionally with its own all/some/none checkbox. */
function NavRow({
  label,
  icon: Icon,
  count,
  active,
  strong,
  onClick,
  check,
}: {
  label: string;
  icon?: LucideIcon;
  count: string | null;
  active: boolean;
  strong: boolean;
  onClick: () => void;
  check?: { state: Tri; onToggle: (on: boolean) => void };
}) {
  return (
    <div
      className={`flex items-center gap-2.5 border-b border-rule/60 border-l-2 pr-3 pl-3.5 transition-colors ${
        active ? "border-l-brand bg-brand-light/70" : "border-l-transparent hover:bg-ink/[0.03]"
      }`}
    >
      {check ? <TriBox label={`All of ${label}`} onToggle={check.onToggle} state={check.state} /> : null}
      <button
        aria-current={active || undefined}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-2.5 text-left"
        onClick={onClick}
        type="button"
      >
        {Icon ? <Icon aria-hidden="true" className={`size-4 shrink-0 ${active ? "text-brand-ink" : "text-muted"}`} /> : null}
        <span
          className={`min-w-0 flex-1 truncate text-sm ${active ? "font-semibold text-brand-ink" : strong ? "font-medium text-ink" : "text-muted"}`}
        >
          {label}
        </span>
        {count ? (
          <span className="shrink-0 rounded-full bg-brand-light px-2 py-0.5 text-[11px] font-semibold text-brand-ink tabular-nums">
            {count}
          </span>
        ) : null}
        <ChevronRight aria-hidden="true" className="size-3.5 shrink-0 text-muted/60" />
      </button>
    </div>
  );
}

/** A checkbox with a label; `bare` drops the row chrome for use inside another row. */
function CheckRow({
  label,
  state,
  onToggle,
  bare = false,
}: {
  label: string;
  state: Tri;
  onToggle: (on: boolean) => void;
  bare?: boolean;
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-2.5 text-sm ${state === "none" ? "text-muted" : "text-ink"} ${
        bare ? "min-w-0" : "min-h-11 border-b border-rule/60 px-4 font-medium"
      }`}
    >
      <TriBox onToggle={onToggle} state={state} />
      <span className="min-w-0">{label}</span>
    </label>
  );
}

/** A native checkbox that can also show "some" (indeterminate). */
function TriBox({ state, onToggle, label }: { state: Tri; onToggle: (on: boolean) => void; label?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <input
      aria-label={label}
      checked={state === "all"}
      className="size-4 shrink-0 cursor-pointer accent-[var(--gq-color-1)]"
      onChange={() => onToggle(state !== "all")}
      ref={ref}
      type="checkbox"
    />
  );
}
